import React from "react";
import {
  BadgeCheck,
  Facebook,
  Instagram,
  Linkedin,
  Github,
  Globe,
  Mail,
  MoreHorizontal,
  MapPin,
  UserCircle,
  Tag,
} from "lucide-react";
import { Dropdown } from "Common/Components/Dropdown";

interface propsType {
  className: string;
  contact: any;
}

const HeaderInfo = ({ className, contact }: propsType) => {
  // Function to get initials from first and last name
  const getInitials = (firstName: string = "", lastName: string = "") => {
    const firstInitial = firstName?.charAt(0)?.toUpperCase() || '';
    const lastInitial = lastName?.charAt(0)?.toUpperCase() || '';
    return firstInitial + lastInitial;
  };

  // Function to generate a consistent color based on the name
  const getAvatarColor = (name: string = "") => {
    const colors = [
      'bg-red-500', 'bg-blue-500', 'bg-green-500', 'bg-yellow-500', 
      'bg-purple-500', 'bg-pink-500', 'bg-indigo-500', 'bg-teal-500'
    ];
    const index = name.length % colors.length;
    return colors[index];
  };

  const initials = getInitials(contact?.firstName, contact?.lastName);
  const avatarColor = getAvatarColor(contact?.firstName + contact?.lastName);

  return (
    <div
      className={`max-w-5xl mx-auto bg-white dark:bg-zink-700 rounded-2xl shadow-sm border border-slate-100 dark:border-zink-600 p-6 ${className}`}
    >
      <div className="flex flex-col items-center gap-6 md:flex-row">
        {/* Profile Avatar with Initials */}
        <div className="relative">
          <div className={`size-24 md:size-28 rounded-full shadow-md flex items-center justify-center text-white text-2xl md:text-3xl font-semibold ${avatarColor}`}>
            {initials || <UserCircle className="size-12 text-white/80" />}
          </div>
        </div>

        {/* Info Section */}
        <div className="flex-1 text-center md:text-left">
          <div className="flex flex-col items-center justify-between gap-2 md:flex-row">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              {`${contact?.firstName || ''} ${contact?.lastName || ''}`.trim() || 'Unnamed Contact'}
            </h2>
          </div>

          {/* Subtitle */}
          <div className="flex flex-wrap justify-center gap-4 mt-2 text-slate-500 md:justify-start">
            {contact?.contactType && (
              <p className="flex items-center gap-1 text-sm">
                <UserCircle className="size-4" /> {contact.contactType}
              </p>
            )}
            {contact?.streetAddress && (
              <p className="flex items-center gap-1 text-sm">
                <MapPin className="size-4" /> {contact.streetAddress}
              </p>
            )}
          </div>

          {/* Tags Section */}
          {contact?.tags && contact.tags.length > 0 && (
            <div className="flex flex-wrap justify-center gap-2 mt-3 md:justify-start">
              <div className="flex items-center gap-1 text-sm text-slate-500">
                <Tag className="size-4" />
                <span className="font-medium">Tags:</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {contact.tags.map((tag: string, index: number) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-custom-50 text-custom-600 border border-custom-200 dark:bg-custom-500/10 dark:text-custom-400 dark:border-custom-500/20"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Socials */}
          <div className="flex flex-wrap justify-center gap-2 mt-5 md:justify-start">
            {[
              {
                icon: Facebook,
                color: "text-sky-500 bg-sky-100 hover:bg-sky-200 dark:bg-sky-500/10 dark:hover:bg-sky-500/20",
              },
              {
                icon: Instagram,
                color: "text-pink-500 bg-pink-100 hover:bg-pink-200 dark:bg-pink-500/10 dark:hover:bg-pink-500/20",
              },
              {
                icon: Linkedin,
                color: "text-blue-500 bg-blue-100 hover:bg-blue-200 dark:bg-blue-500/10 dark:hover:bg-blue-500/20",
              },
              {
                icon: Github,
                color: "text-slate-600 bg-slate-100 hover:bg-slate-200 dark:bg-slate-500/10 dark:hover:bg-slate-500/20 dark:text-slate-400",
              },
              {
                icon: Globe,
                color: "text-red-500 bg-red-100 hover:bg-red-200 dark:bg-red-500/10 dark:hover:bg-red-500/20",
              },
            ].map(({ icon: Icon, color }, idx) => (
              <a
                key={idx}
                href="#!"
                className={`flex items-center justify-center size-9 rounded-lg transition ${color}`}
              >
                <Icon className="size-4" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeaderInfo;